import React, { FormEvent, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { useTranslation } from "next-i18next";

import { BooleanStringV2 } from "@sellernote/shared/src/api/dto/entities/common/common";
import SUPPORT_QUERY from "@sellernote/shared/src/api/queries/forwarding/SUPPORT_QUERY";
import { ShipdaCurrentLanguage } from "@sellernote/shared/src/i18n/i18nForShipda";
import { noop } from "@sellernote/shared/src/utils/etc";
import { useCheckIsMobile } from "@sellernote/shared/src/utils/hook";
import {
  getValueAsNumberOnly,
  validateEmail,
  validatePhone,
} from "@sellernote/shared/src/utils/validation";
import { getReverseBooleanString } from "@sellernote/features/src/common/utils/options";
import useCheckIsFulfillmentPage from "@sellernote/shipda-kr/src/hooks/common/useCheckIsFulfillmentPage";
import Button from "@sellernote/ui/src/sds-v2/components/button/Button";
import TextButton from "@sellernote/ui/src/sds-v2/components/button/TextButton";
import InputTextField from "@sellernote/ui/src/sds-v2/components/form/InputTextField";
import SelectButton from "@sellernote/ui/src/sds-v2/components/form/SelectButton";
import TextAreaField from "@sellernote/ui/src/sds-v2/components/form/TextAreaField";
import Modal from "@sellernote/ui/src/sds-v2/components/modal/Modal";
import FourChevronRightIcon from "@sellernote/ui/src/sds-v2/components/svgIcons/FourChevronRightIcon";

import CurrentAd from "../../policy/ad/CurrentAd";
import CurrentPrivacyPolicy from "../../policy/privacy_policy/CurrentPrivacyPolicy";
import { RequestServiceGuideUiType, ServiceGuideRequestForm } from "./index";
import Styled from "./index.styles";

export default function RequestGuideForm({
  form,
  setForm,
  setOpensSuccessRequestGuideModal,
  resetRequestGuideForm,
  agreesPrivacyPolicy,
  setAgreesPrivacyPolicy,
  agreesAdEmails,
  setAgreesAdEmails,
  uiType,
  isCustomStyle,
}: {
  form: ServiceGuideRequestForm;
  setForm: (v: ServiceGuideRequestForm) => void;
  setOpensSuccessRequestGuideModal: (v: boolean) => void;
  resetRequestGuideForm: () => void;
  agreesPrivacyPolicy: BooleanStringV2;
  setAgreesPrivacyPolicy: (v: BooleanStringV2) => void;
  agreesAdEmails: BooleanStringV2;
  setAgreesAdEmails: (v: BooleanStringV2) => void;
  uiType: RequestServiceGuideUiType;
  isCustomStyle: boolean;
}) {
  const [opensPrivacyPolicyModal, setOpensPrivacyPolicyModal] = useState(false);
  const [opensAdEmailTermsModal, setOpensAdEmailTermsModal] = useState(false);

  const { t } = useTranslation(["main-landing", "components"]);

  const [
    opensSuccessModalOfAlreadyMember,
    setOpensSuccessModalOfAlreadyMember,
  ] = useState(false);

  const { isMobile } = useCheckIsMobile();

  const {
    isFulfillmentPage,
    isFulfillmentEventPage,
    isFulfillmentDomesticOrOverseasPage,
  } = useCheckIsFulfillmentPage();

  const {
    mutate: requestGuide,
    ResponseHandler: ResponseHandlerOfRequestGuide,
  } = SUPPORT_QUERY.useRequestGuide({
    onSuccess: ({ isEmailJoined, isPhoneJoined }) => {
      // 둘 중 하나라도 가입 내역이 있으면 가입 모달을 띄우지 않는다.
      if (isEmailJoined || isPhoneJoined)
        return setOpensSuccessModalOfAlreadyMember(true);

      setOpensSuccessRequestGuideModal(true);
    },
  });

  const createRequestGuideFormChangeHandler =
    (key: string) => (value: string) => {
      setForm({ ...form, [key]: value });
    };

  const agreesAllCheck =
    agreesPrivacyPolicy === "TRUE" && agreesAdEmails === "TRUE"
      ? "TRUE"
      : "FALSE";

  const handleAllCheck = (val: BooleanStringV2) => {
    if (val === "TRUE") {
      setAgreesPrivacyPolicy("FALSE");
      setAgreesAdEmails("FALSE");
      return;
    }

    setAgreesPrivacyPolicy("TRUE");
    setAgreesAdEmails("TRUE");
  };

  const validatesForm = useMemo(() => {
    const { name, company, email, phone, type } = form;

    const isCompletedForm = name && company && email && phone && type;

    const isValidEmail = validateEmail({
      val: form.email,
      allowEmpty: true,
      Trans,
    }).result;

    const isValidPhone = validatePhone({
      val: form.phone,
      allowEmpty: true,
      Trans,
    }).result;

    const isCheckedAgreesAll = agreesAllCheck === "TRUE";

    return (
      isCompletedForm && isValidEmail && isValidPhone && isCheckedAgreesAll
    );
  }, [agreesAllCheck, form]);

  const handleRequestGuideSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!validatesForm) return;

    requestGuide({
      type: form.type,
      userComment: form.userComment,
      userInfo: {
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
      },
    });
  };

  return (
    <>
      <Styled.requestGuideForm
        onSubmit={handleRequestGuideSubmit}
        isCustomStyle={isCustomStyle}
      >
        <div className="input-row-container">
          <InputTextField
            labelInfo={{
              label: (
                <>
                  <Trans i18nKey="main-landing:소개서신청폼_회사명" />

                  <span className="required">
                    {t("main-landing:소개서신청폼_필수입력")}
                  </span>
                </>
              ),
            }}
            value={form.company}
            setValue={createRequestGuideFormChangeHandler("company")}
            className="custom-landing-input-text-field"
          />

          <InputTextField
            labelInfo={{
              label: (
                <>
                  <Trans i18nKey="main-landing:소개서신청폼_성함" />

                  <span className="required">
                    {t("main-landing:소개서신청폼_필수입력")}
                  </span>
                </>
              ),
            }}
            value={form.name}
            setValue={createRequestGuideFormChangeHandler("name")}
            className="custom-landing-input-text-field"
          />

          <InputTextField
            labelInfo={{
              label: (
                <>
                  <Trans i18nKey="main-landing:소개서신청폼_연락처" />

                  <span className="required">
                    {t("main-landing:소개서신청폼_필수입력")}
                  </span>
                </>
              ),
            }}
            value={form.phone}
            setValue={(v) =>
              createRequestGuideFormChangeHandler("phone")(
                getValueAsNumberOnly(v)
              )
            }
            errorMessage={
              validatePhone({
                val: form.phone,
                allowEmpty: true,
                Trans,
              }).message
            }
            className="custom-landing-input-text-field"
          />

          <InputTextField
            labelInfo={{
              label: (
                <>
                  <Trans i18nKey="main-landing:소개서신청폼_이메일" />

                  <span className="required">
                    {t("main-landing:소개서신청폼_필수입력")}
                  </span>
                </>
              ),
            }}
            value={form.email}
            setValue={createRequestGuideFormChangeHandler("email")}
            errorMessage={
              validateEmail({
                val: form.email,
                allowEmpty: true,
                Trans,
              }).message
            }
            className="custom-landing-input-text-field"
          />
        </div>

        <span className="guide">
          {isFulfillmentPage ? (
            <Trans i18nKey="components:landing.요청할_항목을_선택해주세요." />
          ) : (
            <Trans i18nKey="main-landing:소개서신청폼_종류" />
          )}

          <span className="required">
            {t("main-landing:소개서신청폼_필수입력")}
          </span>
        </span>

        <div className="radio-button-row-container">
          <SelectButton
            uiType="radio"
            size={isCustomStyle ? "default" : "large"}
            label={
              isFulfillmentPage
                ? t("components:landing.소개서")
                : t("main-landing:소개서신청폼_종류_소개서")
            }
            value={
              isFulfillmentPage ? "fulfillmentIntroduction" : "serviceGuide"
            }
            selected={
              isFulfillmentPage
                ? form.type === "fulfillmentIntroduction"
                : form.type === "serviceGuide"
            }
            onSelect={createRequestGuideFormChangeHandler("type")}
            className="custom-landing-radio-button"
          />

          <SelectButton
            uiType="radio"
            size={isCustomStyle ? "default" : "large"}
            label={
              isFulfillmentEventPage
                ? t("components:landing.이벤트_견적서")
                : isFulfillmentDomesticOrOverseasPage
                  ? t("components:landing.표준_견적서")
                  : t("main-landing:소개서신청폼_종류_가이드북")
            }
            value={
              isFulfillmentEventPage
                ? "fulfillmentEventEstimation"
                : isFulfillmentDomesticOrOverseasPage
                  ? "fulfillmentEstimation"
                  : "serviceGuideBook"
            }
            selected={
              isFulfillmentEventPage
                ? form.type === "fulfillmentEventEstimation"
                : isFulfillmentDomesticOrOverseasPage
                  ? form.type === "fulfillmentEstimation"
                  : form.type === "serviceGuideBook"
            }
            onSelect={createRequestGuideFormChangeHandler("type")}
            className="custom-landing-radio-button"
          />

          <SelectButton
            uiType="radio"
            size={isCustomStyle ? "default" : "large"}
            label={t("main-landing:소개서신청폼_종류_모두받기")}
            value={
              isFulfillmentEventPage
                ? "fulfillmentEventGuide"
                : isFulfillmentDomesticOrOverseasPage
                  ? "fulfillmentGuide"
                  : "allServiceGuide"
            }
            selected={
              isFulfillmentEventPage
                ? form.type === "fulfillmentEventGuide"
                : isFulfillmentDomesticOrOverseasPage
                  ? form.type === "fulfillmentGuide"
                  : form.type === "allServiceGuide"
            }
            onSelect={createRequestGuideFormChangeHandler("type")}
            className="custom-landing-radio-button"
          />
        </div>

        <div className="text-area-row-container">
          <TextAreaField
            labelInfo={{
              label: t("main-landing:소개서신청폼_문의사항"),
            }}
            value={form.userComment}
            setValue={(val) => {
              if (val.length > 500) return;

              createRequestGuideFormChangeHandler("userComment")(val);
            }}
            placeholder={t("main-landing:소개서신청폼_문의사항_입력안내")}
          />
        </div>

        <div className="checkbox-row-container">
          <SelectButton
            uiType="checkboxFilled"
            size={isCustomStyle ? "default" : "large"}
            label={t("main-landing:소개서신청폼_약관모두동의")}
            value={agreesAllCheck}
            selected={agreesAllCheck === "TRUE"}
            onSelect={handleAllCheck}
            className="custom-landing-checkbox"
          />

          <SelectButton
            uiType="checkboxFilled"
            size={isCustomStyle ? "default" : "large"}
            label={
              <>
                {t("main-landing:소개서신청폼_개인정보수집동의")}

                <TextButton
                  theme="white"
                  buttonType="button"
                  fontSize={isCustomStyle ? 10 : 14}
                  label={t("main-landing:소개서신청폼_약관_자세히보기")}
                  handleClick={() => setOpensPrivacyPolicyModal(true)}
                  className="custom-checkbox-text-button"
                />
              </>
            }
            value={agreesPrivacyPolicy}
            selected={agreesPrivacyPolicy === "TRUE"}
            onSelect={(v) => setAgreesPrivacyPolicy(getReverseBooleanString(v))}
            className="custom-landing-checkbox sub-checkbox"
          />

          <SelectButton
            uiType="checkboxFilled"
            size={isCustomStyle ? "default" : "large"}
            label={
              <>
                {t("main-landing:소개서신청폼_광고성메일수신동의")}

                <TextButton
                  theme="white"
                  buttonType="button"
                  fontSize={isCustomStyle ? 10 : 14}
                  label={t("main-landing:소개서신청폼_약관_자세히보기")}
                  handleClick={() => setOpensAdEmailTermsModal(true)}
                  className="custom-checkbox-text-button"
                />
              </>
            }
            value={agreesAdEmails}
            selected={agreesAdEmails === "TRUE"}
            onSelect={(v) => setAgreesAdEmails(getReverseBooleanString(v))}
            className="custom-landing-checkbox sub-checkbox"
          />
        </div>

        <Button
          theme="tertiary"
          borderType="filled"
          size="normal"
          width={uiType === "floating" ? "100%" : "auto"}
          label={t("main-landing:소개서신청폼_소개서요청_버튼")}
          {...(!isMobile && {
            iconInfo: {
              Icon: FourChevronRightIcon,
              position: "right",
            },
          })}
          handleClick={noop}
          disabled={!validatesForm}
        />
      </Styled.requestGuideForm>

      {ResponseHandlerOfRequestGuide}

      <Modal
        uiType="onlyDescription"
        active={opensSuccessModalOfAlreadyMember}
        desc={t("main-landing:소개서신청폼_성공_기가입자_모달")}
        actionPositive={{
          label: t("main-landing:소개서신청폼_성공_기가입자_모달_버튼"),
          handleClick: () => {
            setOpensSuccessModalOfAlreadyMember(false);

            resetRequestGuideForm();
          },
        }}
      />

      <Modal
        uiType="formInput"
        active={opensPrivacyPolicyModal}
        title={
          <Styled.termsTitle>
            {t("main-landing:소개서신청폼_약관_모달_개인정보수집동의_타이틀")}{" "}
            <span className="required">
              {t("main-landing:소개서신청폼_약관_모달_타이틀_필수")}
            </span>
          </Styled.termsTitle>
        }
        body={
          <Styled.termsContainer>
            <CurrentPrivacyPolicy
              layoutSize="default"
              language={ShipdaCurrentLanguage.currentLanguage}
            />
          </Styled.termsContainer>
        }
        onClose={() => {
          setOpensPrivacyPolicyModal(false);
        }}
        width="960px"
      />

      <Modal
        uiType="formInput"
        active={opensAdEmailTermsModal}
        title={
          <Styled.termsTitle>
            {t("main-landing:소개서신청폼_약관_모달_광고성메일수신동의_타이틀")}{" "}
            <span className="required">
              {t("main-landing:소개서신청폼_약관_모달_타이틀_필수")}
            </span>
          </Styled.termsTitle>
        }
        body={
          <Styled.termsContainer>
            <CurrentAd
              layoutSize="default"
              language={ShipdaCurrentLanguage.currentLanguage}
            />
          </Styled.termsContainer>
        }
        onClose={() => {
          setOpensAdEmailTermsModal(false);
        }}
        width="960px"
      />
    </>
  );
}
